import {Injectable} from '@angular/core';
import {RestClientService} from '../../../core/services/rest-client.service';
import {BehaviorSubject, catchError, finalize, map, Observable, of, shareReplay, tap} from 'rxjs';
import {NGXLogger} from 'ngx-logger';
import {RestResponse} from '../../../core/rest-response';
import {User} from '../model/user';

// 로그인 흔적 hint — auth-refresh.interceptor 의 SESSION_HINT_KEY 와 동일 키.
// (인터셉터가 UserService 를 import 하므로 순환 의존 회피 위해 키는 리터럴로 중복 정의.)
const SESSION_HINT_KEY = 'hasSession';

@Injectable({ providedIn: 'root' })
export class UserService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  // /users/me 동시 호출 de-dup (app.component.ngOnInit + authGuard race 방지)
  private fetchInFlight$: Observable<User | null> | null = null;

  constructor(private readonly restClient: RestClientService,
              private readonly log: NGXLogger) {
  }

  public existsUsername(username: string): Observable<any>{
    return this.restClient.get('/exists/username', {username})
  }

  public existsMobile(mobile: string): Observable<any>{
    return this.restClient.get('/exists/mobile', {mobile})
  }

  public sendConfirmationCodeForSignup(mobile: string): Observable<any> {
    return this.restClient.post('/confirmation/code', { mobile, type: 'SIGNUP' });
  }

  public sendConfirmationCodeForSnsSignup(mobile: string): Observable<any> {
    return this.restClient.post('/confirmation/code', { mobile, type: 'SIGNUP_SNS' });
  }

  /**
   * @param type 'USERNAME' (아이디 찾기) | 'RESET' (비밀번호 초기화 — 분실 시 임시비번 재발급)
   */
  public sendConfirmationCodeForFind(mobile: string, type: string): Observable<any> {
    return this.restClient.post('/confirmation/code', { mobile, type });
  }

  /**
   * 비밀번호 초기화(분실) 인증번호 발송.
   * 아이디(이메일)와 휴대폰이 같은 계정에 묶여 있을 때만 서버가 SMS 를 발송한다.
   * 짝이 안 맞으면 서버가 거부하므로 엉뚱한 번호로 인증번호가 나가지 않는다.
   */
  public sendResetCode(username: string, mobile: string): Observable<any> {
    return this.restClient.post('/reset/code', { username, mobile });
  }

  public verifySignupCode(mobile: string, code: string): Observable<any> {
    return this.restClient.post('/signup/verify', { mobile, code });
  }

  public verifySnsSignup(mobile: string, code: string): Observable<any> {
    return this.restClient.post('/signup/sns/verify', { mobile, code }, { withCredentials: true });
  }

  public findUsername(mobile: string, code: string): Observable<any> {
    return this.restClient.post('/find/username', { mobile, code });
  }

  public resetPassword(username: string, mobile: string, code: string): Observable<any> {
    return this.restClient.post('/reset/password', { username, mobile, code });
  }

  public signin(request: any): Observable<RestResponse> {
    return this.restClient.post('/signin', request)
      .pipe(
        tap((response: RestResponse) => {
          localStorage.setItem(SESSION_HINT_KEY, 'true'); // 로그인 성공 흔적 → 이후 reissue 허용
          if (response.data) {
             this.currentUserSubject.next(response.data);
          } else {
             this.fetchCurrentUser().subscribe();
          }
        })
      );
  }

  public jwtTest(): Observable<RestResponse> {
    return this.restClient.get('/jwt/test', undefined, { withCredentials: true })
  }

  public createUser(request: any): Observable<boolean> {
    this.log.debug('사용자 생성', request);
    return this.restClient.post('/signup', request)
      .pipe(map((response: RestResponse) => {
        return true;
      }));
  }

  public fetchCurrentUser(): Observable<User | null> {
    if (this.fetchInFlight$) {
      return this.fetchInFlight$;
    }
    this.fetchInFlight$ = this.restClient.get('/users/me', undefined, { withCredentials: true })
      .pipe(
        map((response: RestResponse) => {
          if (response.success && response.data) {
            this.currentUserSubject.next(response.data);
            return response.data;
          } else {
            this.currentUserSubject.next(null);
            return null;
          }
        }),
        catchError(() => {
            this.currentUserSubject.next(null);
            return of(null);
        }),
        finalize(() => { this.fetchInFlight$ = null; }),
        shareReplay(1)
      );
    return this.fetchInFlight$;
  }

  public modifyPassword(payload: { currentPassword: string; newPassword: string; confirmPassword: string }): Observable<RestResponse> {
    return this.restClient.put('/users/password', payload);
  }

  /**
   * 휴대폰 번호 변경 1단계 — 변경할 '새 번호'로 MOBILE 인증번호 발송.
   * 이미 사용 중인 번호면 서버가 거부한다(타인 번호로의 OTP 발송 차단).
   */
  public sendMobileChangeCode(mobile: string): Observable<RestResponse> {
    return this.restClient.post('/users/mobile/code', { mobile });
  }

  /**
   * 휴대폰 번호 변경 2단계 — 1단계에서 받은 인증번호(code)로 새 번호의 소유를 검증한 뒤 변경.
   * 서버가 MOBILE OTP 를 검증하므로 code 없이는 변경되지 않는다.
   */
  public modifyMobile(payload: { mobile: string; code: string }): Observable<RestResponse> {
    return this.restClient.put('/users/mobile', payload);
  }

  public signoff(): Observable<any> {
    return this.restClient.post('/signoff', {}, { withCredentials: true }).pipe(
      tap(() => {
        this.currentUserSubject.next(null);
        localStorage.removeItem(SESSION_HINT_KEY);
      }),
      catchError(() => {
        this.currentUserSubject.next(null);
        localStorage.removeItem(SESSION_HINT_KEY);
        return of(null);
      })
    );
  }

  public reissueToken(): Observable<RestResponse> {
    return this.restClient.post('/signin/reissue', {}, { withCredentials: true });
  }

  /**
   * 회원 탈퇴 — soft delete + 서버에서 전체 기기 RefreshToken 무효화 + 쿠키 삭제.
   * 성공 시에만 로컬 사용자 상태를 비운다(실패 시 화면은 호출부에서 에러 처리, 리다이렉트하지 않음).
   */
  public deleteUser(): Observable<RestResponse> {
    return this.restClient.delete('/users/me').pipe(
      tap(() => {
        this.currentUserSubject.next(null);
        localStorage.removeItem(SESSION_HINT_KEY); // 탈퇴 = 세션 종료 → hint 제거
      })
    );
  }

  public clearCurrentUser(): void {
    this.currentUserSubject.next(null);
  }

  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }
}
